export type RoleGroup = 'staff' | 'client';

const GROUPED_ROLES = {
  staff: {
    accountant: 'accountant',
    administrator: 'administrator',
    amaiz_support: 'amaiz_support',
    antifraud_specialist: 'antifraud_specialist',
    cashier: 'cashier',
    cfo: 'cfo',
    compliance_manager: 'compliance_manager',
    compliance_specialist: 'compliance_specialist',
    customer_support: 'customer_support',
  },
  client: {
    individual: 'individual',
    individual_onboarding_1: 'individual_onboarding_1',
    individual_onboarding_2: 'individual_onboarding_2',
    individual_onboarding_3: 'individual_onboarding_3',
    individual_onboarding_declined: 'individual_onboarding_declined',

    sole_trader: 'sole_trader',
    sole_trader_onboarding_1: 'sole_trader_onboarding_1',
    sole_trader_onboarding_2: 'sole_trader_onboarding_2',
    sole_trader_onboarding_3: 'sole_trader_onboarding_3',
    sole_trader_onboarding_declined: 'sole_trader_onboarding_declined',

    merchant: 'merchant',
    merchant_onboarding_1: 'merchant_onboarding_1',
    merchant_onboarding_2: 'merchant_onboarding_2',
    merchant_onboarding_3: 'merchant_onboarding_3',
    merchant_onboarding_declined: 'merchant_onboarding_declined',
  },
} as const;

export type ClientRole = keyof typeof GROUPED_ROLES.client;
export type StaffRole = keyof typeof GROUPED_ROLES.staff;

export const ROLES = {
  ...GROUPED_ROLES.client,
  ...GROUPED_ROLES.staff,
} as const;

export type TRole = keyof typeof ROLES;

export type TRoleSimple = 'individual' | 'merchant' | 'cashier';

export const SIMPLE_ROLES: Record<TRoleSimple, TRoleSimple> = {
  individual: 'individual',
  merchant: 'merchant',
  cashier: 'cashier',
};

export const PERSONAL_ONBOARDING_ROLES: TRole[] = [
  ROLES.sole_trader,
  ROLES.individual,
  ROLES.sole_trader_onboarding_1,
  ROLES.sole_trader_onboarding_2,
  ROLES.sole_trader_onboarding_3,
  ROLES.individual_onboarding_1,
  ROLES.individual_onboarding_2,
  ROLES.individual_onboarding_3,
];

export const INDIVIDUAL_TRADER_ONBOARDING_ROLES: TRole[] = [
  ROLES.individual,
  ROLES.individual_onboarding_1,
  ROLES.individual_onboarding_2,
  ROLES.individual_onboarding_3,
];

export const SOLE_TRADER_ONBOARDING_ROLES: TRole[] = [
  ROLES.sole_trader,
  ROLES.sole_trader_onboarding_1,
  ROLES.sole_trader_onboarding_2,
  ROLES.sole_trader_onboarding_3,
];

export const BUSINESS_ONBOARDING_ROLES: TRole[] = [
  ROLES.merchant_onboarding_1,
  ROLES.merchant_onboarding_2,
  ROLES.merchant_onboarding_3,
];

export type NotOnboardingClientRole = Extract<TRole, 'merchant' | 'sole_trader' | 'individual'>;

export const ROLES_ARRAY = Object
  .keys(ROLES)
  .map((key) => ({ value: key as TRole, label: `entity.role.${key}` }));

export const SIMPLE_ROLES_ARRAY = Object
  .keys(SIMPLE_ROLES)
  .map((key) => ({ value: key as TRoleSimple, label: `entity.role.${key}` }));

// RoleGroup

function isRole(value: string): value is TRole {
  return value in ROLES;
}

function isClientRole(role: TRole): role is ClientRole {
  return role in GROUPED_ROLES.client;
}

export function getRoleGroup(role: TRole): RoleGroup;
export function getRoleGroup(role: TRole | undefined | null): RoleGroup | undefined;
export function getRoleGroup(value: string | undefined | null): RoleGroup | undefined;
export function getRoleGroup(value: string | undefined | null): RoleGroup | undefined {
  if (!value || !isRole(value)) return undefined;
  return isClientRole(value) ? 'client' : 'staff';
}
